import { useCallback, useEffect, useMemo, useState } from "react";
import DateInput from "@/components/DateInput";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChatCircleDots, Plus, Package, CheckCircle, Clock, ArrowSquareOut,
  WhatsappLogo, Pencil, Trash, XCircle, Tag, FilePdf, Storefront,
  Warning, Receipt, ArrowsClockwise, Eye,
} from "@phosphor-icons/react";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import ProductoCombobox from "../components/ProductoCombobox";
import EspecificacionCombobox from "../components/EspecificacionCombobox";
import ClienteCombobox from "../components/ClienteCombobox";
import AddressAutocomplete from "../components/AddressAutocomplete";
import api from "../lib/api";
import { imprimirTicketDelivery } from "../lib/printTicket80";
import { ExcelTh, useExcelTable } from "../components/ExcelTable";
import NumberInput from "@/components/ui/NumberInput";

const ESTADOS_LOCAL = [
  { value: "sin_confirmar",       label: "Sin Confirmar",       color: "bg-amber-100 text-amber-800 border-amber-300" },
  { value: "confirmado",          label: "Confirmado",          color: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "en_produccion",       label: "En Producción",       color: "bg-orange-100 text-orange-800 border-orange-300" },
  { value: "listo_para_facturar", label: "Listo p/ Facturar",   color: "bg-purple-100 text-purple-800 border-purple-300" },
  { value: "a_mostrador",         label: "A Mostrador",         color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300", requiresFecha: true },
  { value: "refacturacion_por_diferencias", label: "Refact. por diferencias", color: "bg-sky-100 text-sky-800 border-sky-300" },
  { value: "terminado",           label: "Terminado",           color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { value: "reprogramar",         label: "Reprogramar/Cambio",  color: "bg-rose-100 text-rose-800 border-rose-300" },
  { value: "anulado",             label: "Anulado (NC)",        color: "bg-neutral-200 text-neutral-700 border-neutral-400" },
];

const PAY_STATUS = [
  { value: "pending", label: "Pendiente", cls: "bg-amber-50 text-amber-700" },
  { value: "paid",    label: "Pagado",    cls: "bg-emerald-50 text-emerald-700" },
];

const METODOS_PAGO = ["Efectivo", "Transferencia", "MODO", "Débito", "Crédito", "Mercado Pago", "Otro"];

const fmtMoney = (n, moneda = "ARS") =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: moneda }).format(n || 0);

const fmtPhone = (phone) => {
  if (!phone) return "";
  const d = String(phone).replace(/\D/g, "");
  if (d.startsWith("54") && d.length >= 12) {
    const area = d.slice(2, 4);
    const rest = d.slice(4);
    return `+54 ${area} ${rest.slice(0, 4)}-${rest.slice(4, 8)}`;
  }
  return String(phone);
};

const waLink = (phone, message) => {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  if (!digits) return null;
  let n = digits;
  if (digits.startsWith("54") && !digits.startsWith("549")) n = "549" + digits.slice(2);
  else if (!digits.startsWith("54") && digits.length >= 10) n = "549" + digits;
  const base = `https://wa.me/${n}`;
  return message && message.trim() ? `${base}?text=${encodeURIComponent(message)}` : base;
};

const entregaInfo = (v) => {
  if (v?.entrega_tipo === "pickup") return { label: "Retiro en local", icon: "🏪", cls: "bg-blue-50 text-blue-700 border-blue-200" };
  if (v?.entrega_tipo === "ship")   return { label: "Envío a domicilio", icon: "🚚", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  return { label: "—", icon: "❓", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" };
};

const emptyForm = () => ({
  cliente_id: null,
  cliente_nombre: "", cliente_telefono: "", cliente_email: "", cliente_cuit: "",
  entrega_tipo: "ship",
  direccion_entrega: "",
  direccion_calle: "", direccion_numero: "", direccion_localidad: "", direccion_provincia: "", direccion_cp: "",
  direccion_piso: "", direccion_dpto: "", direccion_timbre: "",
  direccion_lat: null, direccion_lon: null,
  entrega_sucursal: "", entrega_costo: "",
  metodo_pago: "cta_cte", payment_status: "pending",
  codigo_promo: "",  // Cupón manual para desbloquear promos con código
  lista_precios_id: "",
  items: [{ name: "", codigo: "", producto_id: null, quantity: 0, price: 0, sku: "", especificacion: "" }],
  envio: 0, descuento: 0, observaciones: "",
  fecha: new Date().toISOString().slice(0, 10),
  fecha_envio: "",
  horario_entrega_desde: "",
  horario_entrega_hasta: "",
  hora: new Date().toTimeString().slice(0, 5),
});

// Lista predeterminada para pedidos: la marcada como "lista a sincronizar con Tienda Nube"
const listaDefault = (listas) => (listas.find((l) => l.es_lista_tn) || listas[0])?.id || "";

export default function PedidosPage() {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEstado, setFilterEstado] = useState("todos");
  const [filterPayment, setFilterPayment] = useState("todos");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // Form modal (crear / editar)
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Detail modal
  const [detail, setDetail] = useState(null);

  // Delete
  const [delTarget, setDelTarget] = useState(null);

  // Catálogos (productos + listas de precios) y promociones aplicables
  const [productos, setProductos] = useState([]);
  const [listasPrecios, setListasPrecios] = useState([]);
  const [promosAplicables, setPromosAplicables] = useState([]);
  const [promosDescartadas, setPromosDescartadas] = useState(new Set());
  // Medios de pago dinámicos — misma fuente que Ventas (con overrides por usuario)
  const [mediosPago, setMediosPago] = useState([]);
  const [cajas, setCajas] = useState([]);
  // Al editar, evitamos volver a aplicar las promos automáticamente (el descuento ya está en el pedido)
  const [autoAplicarPromos, setAutoAplicarPromos] = useState(true);
  useEffect(() => {
    api.get("/productos").then(({ data }) => setProductos(data || [])).catch(() => {});
    api.get("/listas-precios").then(({ data }) => setListasPrecios(data || [])).catch(() => {});
    api.get("/medios-pago/mios", { params: { scope: "ventas" } })
      .then(({ data }) => setMediosPago(data || []))
      .catch(() => setMediosPago([]));
    api.get("/cajas").then(({ data }) => setCajas(data || [])).catch(() => {});
  }, []);

  // Cambiar default de metodo_pago a "cta_cte" para alinear con la lógica de Ventas.
  // Si el pedido nuevo no tiene medio de pago elegido y hay medios cargados,
  // pre-seleccionamos "cta_cte" (queda pendiente hasta facturación).
  useEffect(() => {
    if (formOpen && !editingId && (form.metodo_pago === "Efectivo" || !form.metodo_pago)) {
      // Solo pisamos si es el valor legacy default; respetamos elecciones manuales.
      if (form.metodo_pago === "Efectivo") {
        setForm((f) => ({ ...f, metodo_pago: "cta_cte" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOpen, editingId]);

  // Set default lista_precios cuando abrimos el modal si aún no hay
  useEffect(() => {
    if (formOpen && !form.lista_precios_id && listasPrecios.length > 0) {
      setForm(f => ({ ...f, lista_precios_id: listaDefault(listasPrecios) }));
    }
  }, [formOpen, form.lista_precios_id, listasPrecios]);

  // Subtotal auto-recalculado
  const formSubtotal = useMemo(
    () => form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.price) || 0), 0),
    [form.items]
  );

  // Descuento automático por promociones aceptadas
  const promoDescuento = useMemo(() => {
    return promosAplicables
      .filter(p => !promosDescartadas.has(p.promocion_id))
      .reduce((s, p) => s + (Number(p.importe_descuento_total) || 0), 0);
  }, [promosAplicables, promosDescartadas]);

  const descuentoTotal = (Number(form.descuento) || 0) + promoDescuento;
  const formTotal = formSubtotal + (Number(form.envio) || 0) - descuentoTotal;

  // Fetch promos aplicables (debounced) cuando cambian items / lista_precios / medio pago
  useEffect(() => {
    if (!formOpen) return;
    const itemsPayload = form.items
      .filter((it) => (it.producto_id || it.codigo || it.name) && Number(it.quantity) > 0)
      .map((it, idx) => ({
        idx,
        producto_id: it.producto_id || null,
        descripcion: it.name || it.codigo || "",
        precio_unitario: Number(it.price) || 0,
        cantidad: Number(it.quantity) || 0,
        kilos: Number(it.quantity) || 0,
      }));
    if (itemsPayload.length === 0) {
      if (promosAplicables.length) setPromosAplicables([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/promociones/aplicables", {
          cliente_id: null,
          lista_precios_id: form.lista_precios_id || null,
          medio_pago: form.metodo_pago || null,
          codigo: (form.codigo_promo || "").trim() || null,
          fecha: form.fecha,
          subtotal_factura: formSubtotal,
          items: itemsPayload,
        }, { signal: controller.signal });
        setPromosAplicables(data?.promos || []);
        if (!autoAplicarPromos) {
          setPromosDescartadas(new Set((data?.promos || []).map(p => p.promocion_id)));
        }
      } catch { /* silencioso */ }
    }, 400);
    return () => { clearTimeout(timer); controller.abort(); };
    // eslint-disable-next-line
  }, [formOpen, form.items, form.lista_precios_id, form.metodo_pago, form.codigo_promo, form.fecha, autoAplicarPromos]);

  const togglePromo = (id) => {
    setPromosDescartadas(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Helper: convierte un valor guardado en metodo_pago (caja_id, "cta_cte", o texto legacy)
  // a una etiqueta legible ("Efectivo", "Cta Cte", etc.).
  const labelMetodoPago = useCallback((val) => {
    if (!val) return "";
    if (val === "cta_cte") return "Cta Cte";
    const mp = mediosPago.find((m) => m.caja_id === val);
    if (mp) return mp.nombre;
    const cj = cajas.find((c) => c.id === val);
    if (cj) return cj.nombre;
    return String(val); // legacy: "Efectivo", "Transferencia", etc.
  }, [mediosPago, cajas]);

  // WhatsApp templates (compartidos con Tienda Nube)
  const [waTpl, setWaTpl] = useState({ envio: "", retiro: "" });
  useEffect(() => {
    api.get("/tiendanube/wa-templates").then(({ data }) => {
      setWaTpl({ envio: data.envio || "", retiro: data.retiro || "" });
    }).catch(() => {});
  }, []);
  const waLinkForPedido = (v) => {
    if (!v?.cliente_telefono) return null;
    const tpl = v.entrega_tipo === "pickup" ? waTpl.retiro : waTpl.envio;
    return waLink(v.cliente_telefono, tpl);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      // No enviamos `estado` al backend — filtro por estado en cliente para que
      // las stat cards siempre reflejen el total real de cada estado.
      if (filterPayment !== "todos") params.payment_status = filterPayment;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/pedidos", { params });
      setPedidos(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  }, [filterPayment, search]);
  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    total: pedidos.length,
    sin:   pedidos.filter(p => p.estado_local === "sin_confirmar").length,
    conf:  pedidos.filter(p => p.estado_local === "confirmado").length,
    prod:  pedidos.filter(p => p.estado_local === "en_produccion").length,
    most:  pedidos.filter(p => p.estado_local === "a_mostrador").length,
    listo: pedidos.filter(p => p.estado_local === "listo_para_facturar").length,
    term:  pedidos.filter(p => p.estado_local === "terminado").length,
    reprog: pedidos.filter(p => p.estado_local === "reprogramar").length,
    refact: pedidos.filter(p => p.estado_local === "refacturacion_por_diferencias").length,
  }), [pedidos]);

  // Filtro por estado aplicado en cliente
  const filteredPedidos = useMemo(() => {
    if (filterEstado === "todos") return pedidos;
    return pedidos.filter(p => p.estado_local === filterEstado);
  }, [pedidos, filterEstado]);

  // Sort + filtros tipo Excel por columna.
  const excelAccessors = useMemo(() => ({
    numero: (r) => r.numero,
    fecha: (r) => r.fecha,
    cliente: (r) => r.cliente_nombre || "",
    telefono: (r) => r.cliente_telefono || "",
    entrega: (r) => entregaInfo(r).label,
    fecha_envio: (r) => r.fecha_envio || "",
    pago: (r) => labelMetodoPago(r.metodo_pago) || "",
    total: (r) => Number(r.total) || 0,
    estado_local: (r) => (ESTADOS_LOCAL.find((e) => e.value === r.estado_local) || { label: r.estado_local || "—" }).label,
  }), [labelMetodoPago]);

  const {
    sortedRows: displayPedidos, sort, toggleSort,
    filters: excelFilters, setFilter: setExcelFilter, clearFilter: clearExcelFilter,
    uniqueValues: excelUnique,
  } = useExcelTable(filteredPedidos, excelAccessors);

  // ── Form helpers ────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setPromosAplicables([]);
    setPromosDescartadas(new Set());
    setAutoAplicarPromos(true);
    setFormOpen(true);
  };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      cliente_id: p.cliente_id || null,
      cliente_nombre: p.cliente_nombre || "", cliente_telefono: p.cliente_telefono || "",
      cliente_email: p.cliente_email || "", cliente_cuit: p.cliente_cuit || "",
      entrega_tipo: p.entrega_tipo || "ship",
      direccion_entrega: p.direccion_entrega || "",
      direccion_calle: p.direccion_calle || "",
      direccion_numero: p.direccion_numero || "",
      direccion_localidad: p.direccion_localidad || "",
      direccion_provincia: p.direccion_provincia || "",
      direccion_cp: p.direccion_cp || "",
      direccion_piso: p.direccion_piso || "",
      direccion_dpto: p.direccion_dpto || "",
      direccion_timbre: p.direccion_timbre || "",
      direccion_lat: p.direccion_lat ?? null,
      direccion_lon: p.direccion_lon ?? null,
      entrega_sucursal: p.entrega_sucursal || "", entrega_costo: p.entrega_costo ?? "",
      metodo_pago: p.metodo_pago || "Efectivo", payment_status: p.payment_status || "pending",
      codigo_promo: p.codigo_promo || "",
      lista_precios_id: p.lista_precios_id || listaDefault(listasPrecios),
      items: (p.items || []).map(it => ({
        name: it.name || "", codigo: it.sku || "", producto_id: it.producto_id || null,
        quantity: Number(it.quantity) || 0, price: Number(it.price) || 0, sku: it.sku || "",
        especificacion: it.especificacion || "",
      })),
      envio: Number(p.envio) || 0, descuento: Number(p.descuento) || 0,
      observaciones: p.observaciones || "",
      fecha: p.fecha || new Date().toISOString().slice(0, 10),
      fecha_envio: p.fecha_envio || "",
      horario_entrega_desde: p.horario_entrega_desde || "",
      horario_entrega_hasta: p.horario_entrega_hasta || "",
      hora: p.hora || "",
    });
    setPromosDescartadas(new Set());
    setAutoAplicarPromos(false);
    setFormOpen(true);
  };

  const setItem = (i, patch) => setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { name: "", codigo: "", producto_id: null, quantity: 0, price: 0, sku: "", especificacion: "" }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const submitForm = async () => {
    if (!form.cliente_nombre.trim()) {
      toast.error("Ingresá el nombre del cliente");
      return;
    }
    const validItems = form.items.filter(it => it.name.trim() && (Number(it.quantity) > 0));
    if (validItems.length === 0) {
      toast.error("Agregá al menos un ítem con nombre y cantidad > 0");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        fecha: form.fecha,
        hora: form.hora || null,
        cliente_id: form.cliente_id || null,
        cliente_nombre: form.cliente_nombre.trim(),
        cliente_telefono: form.cliente_telefono.trim() || null,
        cliente_email: form.cliente_email.trim() || null,
        cliente_cuit: form.cliente_cuit.trim() || null,
        entrega_tipo: form.entrega_tipo,
        direccion_entrega: form.entrega_tipo === "ship" ? (form.direccion_entrega?.trim() || null) : null,
        direccion_calle: form.entrega_tipo === "ship" ? (form.direccion_calle?.trim() || null) : null,
        direccion_numero: form.entrega_tipo === "ship" ? (form.direccion_numero?.trim() || null) : null,
        direccion_localidad: form.entrega_tipo === "ship" ? (form.direccion_localidad?.trim() || null) : null,
        direccion_provincia: form.entrega_tipo === "ship" ? (form.direccion_provincia?.trim() || null) : null,
        direccion_cp: form.entrega_tipo === "ship" ? (form.direccion_cp?.trim() || null) : null,
        direccion_piso: form.entrega_tipo === "ship" ? (form.direccion_piso?.trim() || null) : null,
        direccion_dpto: form.entrega_tipo === "ship" ? (form.direccion_dpto?.trim() || null) : null,
        direccion_timbre: form.entrega_tipo === "ship" ? (form.direccion_timbre?.trim() || null) : null,
        direccion_lat: form.entrega_tipo === "ship" ? (form.direccion_lat ?? null) : null,
        direccion_lon: form.entrega_tipo === "ship" ? (form.direccion_lon ?? null) : null,
        entrega_sucursal: form.entrega_tipo === "pickup" ? (form.entrega_sucursal.trim() || null) : null,
        entrega_costo: form.entrega_costo === "" ? null : Number(form.entrega_costo),
        metodo_pago: form.metodo_pago,
        payment_status: form.payment_status,
        codigo_promo: (form.codigo_promo || "").trim().toUpperCase() || null,
        items: validItems.map(it => ({
          name: it.name.trim(),
          sku: (it.sku || it.codigo || "").trim() || null,
          producto_id: it.producto_id || null,
          quantity: Number(it.quantity) || 0,
          price: Number(it.price) || 0,
          especificacion: (it.especificacion || "").trim() || null,
        })),
        envio: Number(form.envio) || 0,
        // Sumamos descuento manual + descuentos por promociones aceptadas
        descuento: descuentoTotal,
        observaciones: [
          form.observaciones.trim() || null,
          promosAplicables
            .filter(p => !promosDescartadas.has(p.promocion_id))
            .map(p => `Promo aplicada: ${p.promocion_nombre} (-${new Intl.NumberFormat("es-AR",{style:"currency",currency:"ARS"}).format(p.importe_descuento_total || 0)})`)
            .join(" · ") || null,
        ].filter(Boolean).join("\n") || null,
        fecha_envio: (form.fecha_envio || "").trim() || null,
        horario_entrega_desde: (form.horario_entrega_desde || "").trim() || null,
        horario_entrega_hasta: (form.horario_entrega_hasta || "").trim() || null,
      };
      if (editingId) {
        // Detectar si el pedido original ya había sido enviado/impreso en Mostrador.
        // En ese caso, tras guardar reimprimimos la comanda con header "MODIFICACION DE PEDIDO".
        const originalPed = pedidos.find((x) => x.id === editingId);
        const wasImpreso = !!(originalPed && originalPed.ticket_impreso_at);
        const { data: updated } = await api.put(`/pedidos/${editingId}`, payload);
        toast.success("Pedido actualizado");
        if (wasImpreso) {
          try {
            const pedidoParaImprimir = {
              ...(updated || originalPed || {}),
              // Normalizar campos que espera el renderer del ticket
              cliente_nombre: (updated?.cliente_nombre || originalPed?.cliente_nombre || ""),
              cliente_cuit: (updated?.cliente_cuit || originalPed?.cliente_cuit || ""),
              cliente_telefono: (updated?.cliente_telefono || originalPed?.cliente_telefono || ""),
              direccion: [updated?.direccion_calle, updated?.direccion_numero].filter(Boolean).join(" ") || updated?.direccion_entrega || "",
              localidad: updated?.direccion_localidad || "",
              items: (updated?.items || []).map((it) => ({
                name: it.name || it.descripcion || "",
                quantity: Number(it.quantity || 0),
                price: Number(it.price || 0),
                unidad: it.unidad || "",
                especificacion: it.especificacion || "",
              })),
              subtotal: updated?.subtotal ?? 0,
              envio: updated?.envio ?? 0,
              descuento: updated?.descuento ?? 0,
              total: updated?.total ?? 0,
              metodo_pago: updated?.metodo_pago || "",
              observaciones: updated?.observaciones || "",
            };
            await imprimirTicketDelivery(pedidoParaImprimir, { modificacion: true });
            toast.info("Comanda de modificación enviada a impresión");
          } catch (err) {
            console.error("Error reimprimiendo comanda de modificación:", err);
            toast.error("No se pudo reimprimir la comanda de modificación");
          }
        }
      } else {
        await api.post("/pedidos", payload);
        toast.success("Pedido creado");
      }
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const cambiarEstado = async (p, nuevo) => {
    // Traba UX: no permitir pasar a "en_produccion" sin fecha de envío cargada.
    if (nuevo === "en_produccion" && !(p.fecha_envio || "").trim()) {
      toast.error(
        "Cargá la fecha de envío antes de mandarlo a producción",
        { description: "Sin fecha el pedido puede perderse en el circuito", duration: 6000 },
      );
      return;
    }
    // A Mostrador sólo tiene sentido si el pedido tiene fecha de envío: el POS
    // muestra los pedidos del día en la pestaña Delivery.
    if (nuevo === "a_mostrador" && !(p.fecha_envio || "").trim()) {
      toast.error(
        "Cargá la fecha de envío antes de marcarlo A Mostrador",
        { description: "El POS toma los pedidos por fecha para la pestaña Delivery", duration: 6000 },
      );
      return;
    }
    try {
      await api.patch(`/pedidos/${p.id}/estado`, { estado: nuevo });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar el estado");
    }
  };

  const setFechaEnvio = async (pid, fecha) => {
    // El `<input type="date">` dispara `onChange` en CADA tecla del año
    // (2 → 0002-XX-XX, 20 → 0020-..., etc). Esos valores intermedios fallan
    // la validación del backend (fecha_envio < fecha_pedido). Filtramos hasta
    // que la fecha esté completa (año ≥ 1000) o vacía.
    const clean = (fecha || "").trim();
    if (clean && !/^\d{4}-\d{2}-\d{2}$/.test(clean)) return;
    if (clean && Number(clean.slice(0, 4)) < 1000) return;
    // Optimistic update
    setPedidos((arr) => arr.map((x) => (x.id === pid ? { ...x, fecha_envio: clean || null } : x)));
    try {
      await api.patch(`/pedidos/${pid}/fecha-envio`, { fecha_envio: clean || null });
    } catch (e) {
      toast.error("No se pudo guardar la fecha de envío");
      load();
    }
  };

  const eliminar = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/pedidos/${delTarget.id}`);
      toast.success("Pedido eliminado");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const descargarPDF = async (p) => {
    try {
      // Si el pedido ya fue facturado, el documento es la FACTURA (no el pedido).
      const url_api = p.factura_id && !p.anulada_por_nc_id ? `/afip/pdf/${p.factura_id}` : `/pedidos/${p.id}/pdf`;
      const res = await api.get(url_api, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el PDF");
    }
  };

  // ── Nota de Crédito ─────────────────────────────────────────────
  const [ncTarget, setNcTarget] = useState(null); // pedido a anular
  const [previewPed, setPreviewPed] = useState(null); // pedido a previsualizar (PDF)
  const [ncMotivo, setNcMotivo] = useState("");
  const [ncSubmitting, setNcSubmitting] = useState(false);
  const emitirNC = async () => {
    if (!ncTarget?.factura_id) return;
    setNcSubmitting(true);
    try {
      // Estrategia igual que en FacturasPage:
      // 1) DELETE /facturas/{fid} → si es Remito / no-fiscal, backend auto-genera "NOTA DE CREDITO X"
      //    y responde `{anulada_con_nc: true, ...}`
      // 2) Si backend responde 409 con `requires_nota_credito` (factura A/B/C con CAE) →
      //    POST /facturas/{fid}/nota-credito con motivo, emite NC electrónica en AFIP.
      try {
        const { data } = await api.delete(`/facturas/${ncTarget.factura_id}`);
        if (data?.anulada_con_nc) {
          toast.success(`${data.tipo_comprobante} ${data.nota_credito_numero} generada`);
        } else {
          toast.success("Comprobante anulado");
        }
      } catch (delErr) {
        const status = delErr?.response?.status;
        const detail = delErr?.response?.data?.detail;
        if (status === 409 && detail?.requires_nota_credito) {
          const { data } = await api.post(
            `/ventas/facturas/${ncTarget.factura_id}/nota-credito`,
            { motivo: ncMotivo || undefined, auto_afip: true },
          );
          const nc = data?.nota_credito || {};
          toast.success(`Nota de Crédito ${nc.numero || ""} emitida`);
        } else {
          throw delErr;
        }
      }
      setNcTarget(null);
      setNcMotivo("");
      setDetail(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "No se pudo emitir la Nota de Crédito"));
    } finally {
      setNcSubmitting(false);
    }
  };

  // ── Refacturación por diferencias (post-auditoría) ─────────────────────
  const emitirNCRefact = async (pedido) => {
    if (!pedido?.factura_id) return toast.error("El pedido no tiene factura asociada");
    if (!window.confirm(
      `Se emitirá una Nota de Crédito para anular la factura ${pedido.factura_numero || pedido.factura_id}. ` +
      `Después vas a poder refacturar con los kilos auditados. ¿Continuar?`
    )) return;
    try {
      try {
        const { data } = await api.delete(`/facturas/${pedido.factura_id}`);
        if (data?.anulada_con_nc) {
          toast.success(`${data.tipo_comprobante} ${data.nota_credito_numero} generada`);
        } else {
          toast.success("Comprobante anulado");
        }
      } catch (delErr) {
        const status = delErr?.response?.status;
        const detail = delErr?.response?.data?.detail;
        if (status === 409 && detail?.requires_nota_credito) {
          const { data } = await api.post(
            `/facturas/${pedido.factura_id}/nota-credito`,
            {
              motivo: `Refacturación por Auditoría de Salida — pedido #${pedido.numero}`,
              auto_afip: true,
            },
          );
          const nc = data?.nota_credito || {};
          toast.success(`Nota de Crédito ${nc.numero || ""} emitida`);
        } else {
          throw delErr;
        }
      }
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "No se pudo emitir la NC"));
    }
  };

  const refacturarPedido = (pedido) => {
    // Redirige a Facturas con el prefill del pedido — el endpoint GET
    // /pedidos/{id} ya expone `peso_real_auditado` en items, y FacturasPage
    // lo prioriza sobre kg_producidos para armar la factura con los kilos
    // reales corregidos por auditoría.
    navigate(`/ventas?pedido_id=${pedido.id}`);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800 flex items-center gap-2">
            <ChatCircleDots size={28} weight="fill" className="text-emerald-600" />
            Pedidos
          </h1>
          <p className="text-xs text-neutral-500 mt-0.5">Carga manual · pedidos por WhatsApp, teléfono u otros canales.</p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2"
          data-testid="pedidos-nuevo-btn"
        >
          <Plus size={16} weight="bold" /> Nuevo pedido
        </Button>
      </div>

      {/* Stats (clickeables como filtros) */}
      <div className="grid grid-cols-3 md:grid-cols-9 gap-3">
        <StatCard label="Total" value={stats.total} icon={Package}
          active={filterEstado === "todos"} onClick={() => setFilterEstado("todos")} />
        <StatCard label="Sin confirmar" value={stats.sin} icon={Clock} colorClass="text-amber-600"
          active={filterEstado === "sin_confirmar"} onClick={() => setFilterEstado("sin_confirmar")} />
        <StatCard label="Confirmados" value={stats.conf} icon={CheckCircle} colorClass="text-blue-600"
          active={filterEstado === "confirmado"} onClick={() => setFilterEstado("confirmado")} />
        <StatCard label="En producción" value={stats.prod} icon={Clock} colorClass="text-orange-600"
          active={filterEstado === "en_produccion"} onClick={() => setFilterEstado("en_produccion")} />
        <StatCard label="En mostrador" value={stats.most} icon={Storefront} colorClass="text-fuchsia-600"
          active={filterEstado === "a_mostrador"} onClick={() => setFilterEstado("a_mostrador")} />
        <StatCard label="Listo p/ facturar" value={stats.listo} icon={CheckCircle} colorClass="text-purple-600"
          active={filterEstado === "listo_para_facturar"} onClick={() => setFilterEstado("listo_para_facturar")} />
        <StatCard label="Refact. Diferencias" value={stats.refact} icon={Warning} colorClass="text-sky-700"
          active={filterEstado === "refacturacion_por_diferencias"} onClick={() => setFilterEstado("refacturacion_por_diferencias")} />
        <StatCard label="Terminados" value={stats.term} icon={CheckCircle} colorClass="text-emerald-600"
          active={filterEstado === "terminado"} onClick={() => setFilterEstado("terminado")} />
        <StatCard label="Reprogramar/Cambio" value={stats.reprog} icon={Clock} colorClass="text-rose-600"
          active={filterEstado === "reprogramar"} onClick={() => setFilterEstado("reprogramar")} />
      </div>

      {/* Filtros */}
      <div className="flex gap-2 flex-wrap items-center">
        <Input
          placeholder="Buscar por cliente, teléfono, email o Nº"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs h-9"
          data-testid="pedidos-search"
        />
        <Select value={filterEstado} onValueChange={setFilterEstado}>
          <SelectTrigger className="w-[160px] h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            {ESTADOS_LOCAL.map(e => (<SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={filterPayment} onValueChange={setFilterPayment}>
          <SelectTrigger className="w-[160px] h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los pagos</SelectItem>
            {PAY_STATUS.map(p => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabla */}
      <div className="border border-neutral-300 rounded bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-300">
              <tr className="text-xs uppercase text-neutral-500 font-mono">
                <ExcelTh col="numero" label="Nº" testId="ped-th-numero"
                  sortDir={sort.col === "numero" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("numero")} selected={excelFilters.numero}
                  onFilterChange={(s) => setExcelFilter("numero", s)}
                  onClearFilter={() => clearExcelFilter("numero")} />
                <ExcelTh col="fecha" label="Fecha" testId="ped-th-fecha"
                  sortDir={sort.col === "fecha" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("fecha")} selected={excelFilters.fecha}
                  onFilterChange={(s) => setExcelFilter("fecha", s)}
                  onClearFilter={() => clearExcelFilter("fecha")} />
                <ExcelTh col="cliente" label="Cliente" testId="ped-th-cliente"
                  sortDir={sort.col === "cliente" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("cliente")} selected={excelFilters.cliente}
                  onFilterChange={(s) => setExcelFilter("cliente", s)}
                  onClearFilter={() => clearExcelFilter("cliente")} />
                <ExcelTh col="telefono" label="Teléfono" className="w-44" testId="ped-th-telefono"
                  sortDir={sort.col === "telefono" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("telefono")} selected={excelFilters.telefono}
                  onFilterChange={(s) => setExcelFilter("telefono", s)}
                  onClearFilter={() => clearExcelFilter("telefono")} />
                <ExcelTh col="entrega" label="Entrega" className="w-40" testId="ped-th-entrega"
                  sortDir={sort.col === "entrega" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("entrega")} selected={excelFilters.entrega}
                  onFilterChange={(s) => setExcelFilter("entrega", s)}
                  onClearFilter={() => clearExcelFilter("entrega")} />
                <ExcelTh col="fecha_envio" label="Fecha envío" className="w-36" testId="ped-th-fecha-envio"
                  sortDir={sort.col === "fecha_envio" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("fecha_envio")} selected={excelFilters.fecha_envio}
                  onFilterChange={(s) => setExcelFilter("fecha_envio", s)}
                  onClearFilter={() => clearExcelFilter("fecha_envio")} />
                <ExcelTh col="pago" label="Pago" testId="ped-th-pago"
                  sortDir={sort.col === "pago" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("pago")} selected={excelFilters.pago}
                  onFilterChange={(s) => setExcelFilter("pago", s)}
                  onClearFilter={() => clearExcelFilter("pago")} />
                <ExcelTh col="total" label="Total" align="right" testId="ped-th-total"
                  sortDir={sort.col === "total" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("total")} selected={excelFilters.total}
                  onFilterChange={(s) => setExcelFilter("total", s)}
                  onClearFilter={() => clearExcelFilter("total")} />
                <ExcelTh col="estado_local" label="Estado ERP" align="center" testId="ped-th-estado"
                  sortDir={sort.col === "estado_local" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("estado_local")} selected={excelFilters.estado_local}
                  onFilterChange={(s) => setExcelFilter("estado_local", s)}
                  onClearFilter={() => clearExcelFilter("estado_local")} />
                <th className="text-center px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && displayPedidos.length === 0 && (
                <tr><td colSpan={10} className="text-center py-8 text-neutral-500">Cargando...</td></tr>
              )}
              {!loading && displayPedidos.length === 0 && (
                <tr><td colSpan={10} className="text-center py-10 text-neutral-500">
                  {pedidos.length === 0
                    ? <>Sin pedidos. Hacé click en <strong>Nuevo pedido</strong> para crear el primero.</>
                    : <>Sin resultados para este filtro.</>}
                </td></tr>
              )}
              {displayPedidos.map((p) => {
                const est = ESTADOS_LOCAL.find(e => e.value === p.estado_local) || ESTADOS_LOCAL[0];
                const pay = PAY_STATUS.find(x => x.value === p.payment_status) || { label: p.payment_status || "—", cls: "bg-neutral-100 text-neutral-700" };
                const wa = waLinkForPedido(p);
                const ei = entregaInfo(p);
                return (
                  <tr key={p.id} className="border-b border-neutral-200 hover:bg-neutral-50" data-testid={`ped-row-${p.numero}`}>
                    <td className="px-3 py-2 font-mono font-bold">#{p.numero}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      <div>{p.fecha}</div>
                      {p.hora && <div className="text-[10px] text-neutral-500">{p.hora} hs</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{p.cliente_nombre || "—"}</div>
                      {p.cliente_email && <div className="text-xs text-neutral-500">{p.cliente_email}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {p.cliente_telefono ? (
                        wa ? (
                          <a
                            href={wa} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-[13px] text-emerald-700 hover:text-emerald-800 hover:underline font-mono"
                            title="Abrir chat en WhatsApp"
                            data-testid={`ped-wa-${p.numero}`}
                          >
                            <WhatsappLogo size={14} weight="fill" className="text-emerald-600" />
                            {fmtPhone(p.cliente_telefono)}
                          </a>
                        ) : (
                          <span className="font-mono text-[13px] text-neutral-600">{fmtPhone(p.cliente_telefono)}</span>
                        )
                      ) : (<span className="text-neutral-300">—</span>)}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${ei.cls}`}>
                        {ei.icon} {ei.label}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <DateInput
                        value={p.fecha_envio || ""}
                        min={(p.fecha || p.created_at || "").slice(0, 10) || undefined}
                        onChange={(e) => setFechaEnvio(p.id, e.target.value)}
                        className="text-xs font-mono border border-neutral-300 rounded px-1.5 py-1 w-full focus:border-emerald-500"
                        data-testid={`ped-fecha-envio-${p.numero}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-0.5">
                        <Badge variant="outline" className={`${pay.cls} text-xs font-normal w-fit`}>{pay.label}</Badge>
                        {p.metodo_pago && <span className="text-[10px] text-neutral-500 font-mono">{labelMetodoPago(p.metodo_pago)}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(p.total, p.moneda)}</td>
                    <td className="px-3 py-2 text-center">
                      <Select value={p.estado_local} onValueChange={(nv) => cambiarEstado(p, nv)} disabled={p.estado_local === "anulado"}>
                        <SelectTrigger className={`w-[150px] mx-auto text-xs h-8 border ${est.color} ${p.estado_local === "anulado" ? "opacity-70 cursor-not-allowed" : ""}`} data-testid={`ped-estado-${p.numero}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ESTADOS_LOCAL.map((e) => (
                            <SelectItem key={e.value} value={e.value} disabled={e.value === "anulado"}>{e.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex flex-col items-center gap-1">
                        {p.estado_local === "refacturacion_por_diferencias" ? (
                          <div className="flex flex-col items-center gap-0.5" data-testid={`ped-refact-badges-${p.numero}`}>
                            <Badge variant="outline" className="bg-neutral-100 text-neutral-600 border-neutral-300 text-[10px] line-through" title="Factura original (a anular)">
                              1️⃣ Fact. {p.factura_original_numero || p.factura_numero || ""}
                            </Badge>
                            {p.nota_credito_refact_numero ? (
                              <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-300 text-[10px]" title={`NC emitida: ${p.nota_credito_refact_tipo} ${p.nota_credito_refact_numero}`}>
                                2️⃣ NC {p.nota_credito_refact_numero}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] italic">
                                2️⃣ NC pendiente
                              </Badge>
                            )}
                            {p.factura_refact_numero ? (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" title="Nueva factura ya emitida">
                                3️⃣ Fact. {p.factura_refact_numero}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] italic">
                                3️⃣ Refactura pendiente
                              </Badge>
                            )}
                          </div>
                        ) : p.anulada_por_nc_numero ? (
                          <Badge variant="outline" className="bg-neutral-200 text-neutral-700 border-neutral-400 text-[10px]" title={`Anulada con NC ${p.anulada_por_nc_numero}`}>
                            ⊘ NC {p.anulada_por_nc_numero}
                          </Badge>
                        ) : p.factura_id ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                              ✓ Fact. {p.factura_numero || ""}
                            </Badge>
                            {(p.rec_numeros || []).map((rn) => (
                              <Badge
                                key={rn}
                                variant="outline"
                                className="bg-amber-50 text-amber-800 border-amber-300 text-[10px] font-mono"
                                title={`Remito Electrónico Cárnico ${rn}`}
                                data-testid={`ped-rec-${p.numero}-${rn}`}
                              >
                                REC {rn}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => navigate(`/ventas?pedido_id=${p.id}`)}
                            className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`ped-facturar-${p.numero}`}
                          >
                            Facturar
                          </Button>
                        )}
                        <div className="flex items-center gap-0.5">
                          <Button size="sm" variant="ghost" onClick={() => setDetail(p)} className="h-6 text-[11px] gap-1 px-1.5" data-testid={`ped-ver-${p.numero}`}>
                            <ArrowSquareOut size={12} /> Ver
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPreviewPed(p)}
                            className="h-6 px-1.5 text-slate-700"
                            title="Ver PDF (vista previa)"
                            data-testid={`ped-pdf-ver-${p.numero}`}
                          >
                            <Eye size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => descargarPDF(p)}
                            className="h-6 px-1.5 text-slate-700"
                            title="Descargar PDF"
                            data-testid={`ped-pdf-${p.numero}`}
                          >
                            <FilePdf size={12} />
                          </Button>
                          {p.estado_local !== "terminado" && (
                            <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="h-6 px-1.5 text-blue-600" title="Editar">
                              <Pencil size={12} />
                            </Button>
                          )}
                          {p.estado_local === "refacturacion_por_diferencias" && p.factura_id && (
                            <>
                              {!p.nota_credito_refact_id && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => emitirNCRefact(p)}
                                  className="h-6 px-1.5 text-sky-700 hover:bg-sky-50"
                                  title="Emitir Nota de Crédito (paso 2 de 3)"
                                  data-testid={`ped-nc-${p.numero}`}
                                >
                                  <Receipt size={12} />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => refacturarPedido(p)}
                                disabled={!p.nota_credito_refact_id}
                                className={`h-6 px-1.5 ${p.nota_credito_refact_id ? "text-emerald-700 hover:bg-emerald-50" : "text-neutral-300 cursor-not-allowed"}`}
                                title={p.nota_credito_refact_id
                                  ? "Refacturar con kg auditados (paso 3 de 3)"
                                  : "Primero emití la NC (paso 2) para habilitar la refacturación"}
                                data-testid={`ped-refact-${p.numero}`}
                              >
                                <ArrowsClockwise size={12} />
                              </Button>
                            </>
                          )}
                          {!p.factura_id && (
                            <Button size="sm" variant="ghost" onClick={() => setDelTarget(p)} className="h-6 px-1.5 text-red-500" title="Eliminar">
                              <Trash size={12} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── Modal: Crear / Editar Pedido ─── */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="ped-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar pedido" : "Nuevo pedido"}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-1 -mr-1 space-y-4 text-sm">
            {/* Fecha / hora / entrega */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <Field label="Fecha">
                <DateInput value={form.fecha} onChange={(e) => setForm(f => ({ ...f, fecha: e.target.value }))}
                  className="w-full text-xs border border-neutral-300 rounded px-2 py-1.5 font-mono" data-testid="ped-fecha" />
              </Field>
              <Field label="Hora">
                <input type="time" value={form.hora} onChange={(e) => setForm(f => ({ ...f, hora: e.target.value }))}
                  className="w-full text-xs border border-neutral-300 rounded px-2 py-1.5 font-mono" data-testid="ped-hora" />
              </Field>
              <div className="col-span-2" />
            </div>

            {/* Entrega planificada */}
            <div className="border border-emerald-200 bg-emerald-50/40 rounded p-3">
              <div className="text-[10px] uppercase text-emerald-800 font-mono tracking-widest mb-2">
                Entrega planificada
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <Field label="Fecha de envío">
                  <DateInput
                    value={form.fecha_envio || ""}
                    min={form.fecha || undefined}
                    onChange={(e) => setForm(f => ({ ...f, fecha_envio: e.target.value }))}
                    className="w-full text-xs border border-neutral-300 rounded px-2 py-1.5 font-mono"
                    data-testid="ped-fecha-envio"
                  />
                </Field>
                <Field label="Horario desde">
                  <input
                    type="time"
                    value={form.horario_entrega_desde || ""}
                    onChange={(e) => setForm(f => ({ ...f, horario_entrega_desde: e.target.value }))}
                    className="w-full text-xs border border-neutral-300 rounded px-2 py-1.5 font-mono"
                    data-testid="ped-horario-desde"
                  />
                </Field>
                <Field label="Horario hasta">
                  <input
                    type="time"
                    value={form.horario_entrega_hasta || ""}
                    onChange={(e) => setForm(f => ({ ...f, horario_entrega_hasta: e.target.value }))}
                    className="w-full text-xs border border-neutral-300 rounded px-2 py-1.5 font-mono"
                    data-testid="ped-horario-hasta"
                  />
                </Field>
                <div className="flex items-end">
                  <div className="text-[11px] text-emerald-800 font-mono px-2 py-1 bg-white border border-emerald-200 rounded w-full text-center truncate">
                    {(() => {
                      const d = (form.horario_entrega_desde || "").trim();
                      const h = (form.horario_entrega_hasta || "").trim();
                      if (d && h) return `Entre ${d} y ${h}`;
                      if (d) return `A partir de ${d}`;
                      if (h) return `Hasta ${h}`;
                      return "Sin restricción horaria";
                    })()}
                  </div>
                </div>
              </div>
              <div className="text-[10px] text-emerald-700 mt-2 italic">
                La Hoja de Reparto va a mostrar este horario en cada parada para planificar la ruta.
              </div>
            </div>

            {/* Cliente */}
            <Section title="Cliente">
              <div className="mb-3">
                <Field label="Buscar cliente ya cargado (opcional)">
                  <ClienteCombobox
                    value={form.cliente_id ? { id: form.cliente_id, nombre: form.cliente_nombre, cuit: form.cliente_cuit } : null}
                    onSelect={(c) => {
                      if (!c) {
                        // Limpiar selección → volver a modo "crear nuevo"
                        setForm((f) => ({ ...f, cliente_id: null }));
                        return;
                      }
                      // Pre-cargar todos los datos del cliente elegido
                      setForm((f) => ({
                        ...f,
                        cliente_id: c.id,
                        cliente_nombre: c.nombre || "",
                        cliente_telefono: c.telefono || f.cliente_telefono,
                        cliente_email: c.email || f.cliente_email,
                        cliente_cuit: c.cuit || f.cliente_cuit,
                        direccion_entrega: [c.direccion, c.direccion_numero].filter(Boolean).join(" ") || f.direccion_entrega,
                        direccion_calle: c.direccion || f.direccion_calle,
                        direccion_numero: c.direccion_numero || f.direccion_numero,
                        direccion_localidad: c.localidad || f.direccion_localidad,
                        direccion_provincia: c.provincia || f.direccion_provincia,
                        direccion_cp: c.codigo_postal || f.direccion_cp,
                        lista_precios_id: c.lista_precios_id || f.lista_precios_id,
                      }));
                    }}
                    testId="ped-cliente-combobox"
                  />
                </Field>
                <div className="text-[11px] text-neutral-500 mt-1">
                  Si dejás vacío, al guardar se crea un cliente nuevo automáticamente con los datos que cargues abajo.
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nombre *">
                  <Input value={form.cliente_nombre} onChange={(e) => setForm(f => ({ ...f, cliente_nombre: e.target.value }))} data-testid="ped-cliente-nombre" />
                </Field>
                <Field label="Teléfono (WhatsApp)">
                  <Input value={form.cliente_telefono} onChange={(e) => setForm(f => ({ ...f, cliente_telefono: e.target.value }))} placeholder="+549..." data-testid="ped-cliente-tel" />
                </Field>
                <Field label="Email">
                  <Input type="email" value={form.cliente_email} onChange={(e) => setForm(f => ({ ...f, cliente_email: e.target.value }))} />
                </Field>
                <Field label="DNI / CUIT">
                  <Input value={form.cliente_cuit} onChange={(e) => setForm(f => ({ ...f, cliente_cuit: e.target.value }))} />
                </Field>
              </div>
            </Section>

            {/* Entrega */}
            <Section title="Entrega">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Tipo">
                  <Select value={form.entrega_tipo} onValueChange={(v) => setForm(f => ({ ...f, entrega_tipo: v }))}>
                    <SelectTrigger className="h-9" data-testid="ped-entrega-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ship">🚚 Envío a domicilio</SelectItem>
                      <SelectItem value="pickup">🏪 Retiro en local</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={form.entrega_tipo === "ship" ? "Costo de envío" : "Sucursal"}>
                  {form.entrega_tipo === "ship" ? (
                    <NumberInput min="0" step="0.01" value={form.entrega_costo}
                      onChange={(e) => setForm(f => ({ ...f, entrega_costo: e.target.value, envio: Number(e.target.value) || 0 }))} />
                  ) : (
                    <Input value={form.entrega_sucursal} onChange={(e) => setForm(f => ({ ...f, entrega_sucursal: e.target.value }))} />
                  )}
                </Field>
                {form.entrega_tipo === "ship" && (
                  <div className="col-span-2 space-y-2">
                    <Field label="Buscador de dirección (autocompletado)">
                      <AddressAutocomplete
                        value={form.direccion_entrega}
                        onChange={(v) => setForm(f => ({ ...f, direccion_entrega: v }))}
                        onSelect={(place) => setForm(f => ({
                          ...f,
                          direccion_entrega: place.display_name || [place.direccion_calle, place.direccion_numero].filter(Boolean).join(" "),
                          direccion_calle: place.direccion_calle || f.direccion_calle,
                          direccion_numero: place.direccion_numero || f.direccion_numero,
                          direccion_localidad: place.direccion_localidad || f.direccion_localidad,
                          direccion_provincia: place.direccion_provincia || f.direccion_provincia,
                          direccion_cp: place.direccion_cp || f.direccion_cp,
                          direccion_lat: place.lat != null ? parseFloat(place.lat) : f.direccion_lat,
                          direccion_lon: place.lon != null ? parseFloat(place.lon) : f.direccion_lon,
                        }))}
                        placeholder="Ej: Av. Corrientes 1234, Buenos Aires"
                        testId="ped-direccion-autocomplete"
                      />
                    </Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                      <div className="col-span-3">
                        <Field label="Calle">
                          <Input value={form.direccion_calle} onChange={(e) => setForm(f => ({ ...f, direccion_calle: e.target.value }))}
                            data-testid="ped-direccion-calle" />
                        </Field>
                      </div>
                      <Field label="Número">
                        <Input value={form.direccion_numero} onChange={(e) => setForm(f => ({ ...f, direccion_numero: e.target.value }))}
                          data-testid="ped-direccion-numero" />
                      </Field>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Field label="Localidad">
                        <Input value={form.direccion_localidad} onChange={(e) => setForm(f => ({ ...f, direccion_localidad: e.target.value }))}
                          data-testid="ped-direccion-localidad" />
                      </Field>
                      <Field label="Provincia">
                        <Input value={form.direccion_provincia} onChange={(e) => setForm(f => ({ ...f, direccion_provincia: e.target.value }))}
                          data-testid="ped-direccion-provincia" />
                      </Field>
                      <Field label="Código Postal">
                        <Input value={form.direccion_cp} onChange={(e) => setForm(f => ({ ...f, direccion_cp: e.target.value }))}
                          data-testid="ped-direccion-cp" />
                      </Field>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Field label="Piso">
                        <Input value={form.direccion_piso} onChange={(e) => setForm(f => ({ ...f, direccion_piso: e.target.value }))}
                          placeholder="Ej: 3"
                          data-testid="ped-direccion-piso" />
                      </Field>
                      <Field label="Departamento">
                        <Input value={form.direccion_dpto} onChange={(e) => setForm(f => ({ ...f, direccion_dpto: e.target.value }))}
                          placeholder="Ej: B"
                          data-testid="ped-direccion-dpto" />
                      </Field>
                      <Field label="Timbre">
                        <Input value={form.direccion_timbre} onChange={(e) => setForm(f => ({ ...f, direccion_timbre: e.target.value }))}
                          placeholder="Ej: 12 / rojo"
                          data-testid="ped-direccion-timbre" />
                      </Field>
                    </div>
                  </div>
                )}
              </div>
            </Section>

            {/* Pago + Lista de precios */}
            <Section title="Pago">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Método">
                  <Select value={form.metodo_pago} onValueChange={(v) => setForm(f => ({ ...f, metodo_pago: v }))}>
                    <SelectTrigger className="h-9" data-testid="ped-metodo-pago">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cta_cte">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-sm">
                            CTA CTE
                          </span>
                          <span>Cuenta corriente (pendiente)</span>
                        </div>
                      </SelectItem>
                      {mediosPago.length > 0
                        ? mediosPago.filter((m) => m.caja_id).map((m) => (
                            <SelectItem key={m.id} value={m.caja_id}>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-mono uppercase text-[#666] bg-neutral-100 px-1.5 py-0.5 rounded-sm">
                                  {m.tipo}
                                </span>
                                <span>{m.nombre}</span>
                              </div>
                            </SelectItem>
                          ))
                        : cajas.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-mono uppercase text-[#666] bg-neutral-100 px-1.5 py-0.5 rounded-sm">
                                  {c.tipo}
                                </span>
                                <span>{c.nombre}</span>
                              </div>
                            </SelectItem>
                          ))}
                      {/* Compat con pedidos antiguos: mostrar el valor guardado si no matchea ninguna opción */}
                      {form.metodo_pago && form.metodo_pago !== "cta_cte"
                        && !mediosPago.some((m) => m.caja_id === form.metodo_pago)
                        && !cajas.some((c) => c.id === form.metodo_pago) && (
                          <SelectItem key={form.metodo_pago} value={form.metodo_pago}>
                            <span className="text-[11px] font-mono text-amber-700">Legacy: {form.metodo_pago}</span>
                          </SelectItem>
                        )}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Estado">
                  <Select value={form.payment_status} onValueChange={(v) => setForm(f => ({ ...f, payment_status: v }))}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendiente</SelectItem>
                      <SelectItem value="paid">Pagado</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Lista de precios">
                  <Select
                    value={form.lista_precios_id || ""}
                    onValueChange={(v) => setForm(f => ({ ...f, lista_precios_id: v }))}
                  >
                    <SelectTrigger className="h-9" data-testid="ped-lista-precios"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {listasPrecios.map(l => <SelectItem key={l.id} value={l.id}>{l.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <Field label="Código de descuento (cupón)">
                  <Input
                    value={form.codigo_promo || ""}
                    onChange={(e) => setForm(f => ({ ...f, codigo_promo: e.target.value.toUpperCase() }))}
                    placeholder="Ej: MARZO10"
                    className="h-9 font-mono uppercase"
                    data-testid="ped-codigo-promo"
                  />
                </Field>
                <div className="col-span-2 flex items-end">
                  <div className="text-[10px] text-neutral-500 font-mono">
                    Ingresá un cupón para activar promociones con código. Las promos sin código aplican siempre.
                  </div>
                </div>
              </div>
              <div className="text-[10px] text-neutral-500 mt-1">
                Al seleccionar un producto se usará el precio de esta lista (fallback: precio actual).
              </div>
            </Section>

            {/* Ítems */}
            <Section title="Ítems" right={
              <Button size="sm" variant="outline" onClick={addItem} className="h-7 px-2 text-xs" data-testid="ped-add-item">+ Agregar ítem</Button>
            }>
              <div className="border border-neutral-200 rounded overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-neutral-50 text-[10px] uppercase font-mono text-neutral-500">
                  <div className="col-span-4">Producto</div>
                  <div className="col-span-3">Especificación</div>
                  <div className="col-span-2 text-right">Kilos</div>
                  <div className="col-span-1 text-right">Precio/u</div>
                  <div className="col-span-1 text-right">Subtotal</div>
                  <div className="col-span-1"></div>
                </div>
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 px-2 py-2 border-t border-neutral-200 items-center">
                    <div className="col-span-4">
                      <ProductoCombobox
                        kind="productos"
                        testid={`ped-item-combo-${i}`}
                        value={{ codigo: it.codigo || it.sku, nombre: it.name }}
                        onSelect={(p) => {
                          const prod = productos.find(x => x.codigo === p.codigo) || productos.find(x => x.nombre === p.nombre);
                          const listaId = form.lista_precios_id || listaDefault(listasPrecios);
                          // BUG FIX: nunca caer silenciosamente a otra lista de precios (evita traer precio "Minorista"
                          // cuando el cliente tiene lista mayorista). Si no hay precio en la lista → usar precio_actual
                          // y avisar al operador con un toast.
                          const priceFromList = listaId && prod?.precios ? prod.precios[listaId] : null;
                          const finalPrice = (priceFromList && priceFromList > 0)
                            ? priceFromList
                            : (prod?.precio_actual || p.precio_actual || 0);
                          if (listaId && !(priceFromList && priceFromList > 0)) {
                            const lista = listasPrecios.find(l => l.id === listaId);
                            toast.warning(
                              `"${p.nombre}" no tiene precio en la lista "${lista?.nombre || "seleccionada"}". Se usó precio actual (${fmtMoney(finalPrice)}). Verificá manualmente.`,
                              { duration: 6000 }
                            );
                          }
                          setItem(i, {
                            name: p.nombre,
                            codigo: p.codigo,
                            sku: p.codigo,
                            producto_id: prod?.id || p.id || null,
                            price: finalPrice,
                          });
                        }}
                      />
                    </div>
                    <div className="col-span-3">
                      <EspecificacionCombobox
                        testId={`ped-item-espec-${i}`}
                        value={it.especificacion || ""}
                        onChange={(v) => setItem(i, { especificacion: v })}
                      />
                    </div>
                    <div className="col-span-2">
                      <NumberInput raw min="0" step="0.001" value={it.quantity}
                        onChange={(e) => setItem(i, { quantity: e.target.value })}
                        className="w-full text-right text-xs border border-neutral-300 rounded px-1.5 py-1 font-mono h-8"
                        data-testid={`ped-item-kg-${i}`} />
                    </div>
                    <div className="col-span-1">
                      <NumberInput raw min="0" step="0.01" value={it.price}
                        onChange={(e) => setItem(i, { price: e.target.value })}
                        className="w-full text-right text-xs border border-neutral-300 rounded px-1.5 py-1 font-mono h-8"
                        data-testid={`ped-item-price-${i}`} />
                    </div>
                    <div className="col-span-1 text-right font-mono text-xs">
                      {fmtMoney((Number(it.quantity) || 0) * (Number(it.price) || 0))}
                    </div>
                    <div className="col-span-1 text-right">
                      <button type="button" onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700 text-base leading-none">×</button>
                    </div>
                  </div>
                ))}
                {form.items.length === 0 && (
                  <div className="text-center py-3 text-neutral-400 text-xs">
                    Sin ítems — usá <strong>+ Agregar ítem</strong>
                  </div>
                )}
              </div>
            </Section>

            {/* Panel de promociones aplicables */}
            {promosAplicables.length > 0 && (
              <div className="border border-emerald-300 rounded bg-emerald-50/40" data-testid="ped-promos-panel">
                <div className="px-3 py-2 bg-emerald-100 border-b border-emerald-300 flex items-center gap-2">
                  <Tag size={14} weight="fill" className="text-emerald-700" />
                  <span className="text-[11px] uppercase tracking-widest font-mono text-emerald-800">
                    Promociones aplicables · {promosAplicables.filter(p => !promosDescartadas.has(p.promocion_id)).length} de {promosAplicables.length}
                  </span>
                </div>
                <div className="divide-y divide-emerald-200">
                  {promosAplicables.map((p) => {
                    const active = !promosDescartadas.has(p.promocion_id);
                    const detalle = p.descuento_tipo === "porcentaje"
                      ? `${p.descuento_valor}% de descuento`
                      : p.descuento_tipo === "monto_por_unidad"
                      ? `-${fmtMoney(p.descuento_valor)} por unidad`
                      : p.descuento_tipo === "precio_fijo"
                      ? `Precio fijo ${fmtMoney(p.descuento_valor)}`
                      : null;
                    return (
                      <label key={p.promocion_id} className={`flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer ${active ? "" : "opacity-50"}`}>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox" checked={active}
                            onChange={() => togglePromo(p.promocion_id)}
                            data-testid={`ped-promo-${p.promocion_id}`}
                            className="accent-emerald-600"
                          />
                          <div>
                            <div className="font-medium text-emerald-900">{p.promocion_nombre}</div>
                            {detalle && <div className="text-[11px] text-neutral-500">{detalle}</div>}
                            {p.es_por_medio_pago && (
                              <div className="text-[10px] text-blue-600 uppercase font-mono">Descuento por medio de pago</div>
                            )}
                          </div>
                        </div>
                        <span className="font-mono text-red-600 font-semibold">
                          -{fmtMoney(p.importe_descuento_total || 0)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Totales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-end border border-neutral-200 bg-neutral-50 rounded p-3">
              <div>
                <div className="text-[10px] uppercase text-neutral-500 font-mono">Subtotal</div>
                <div className="font-mono text-sm mt-1">{fmtMoney(formSubtotal)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-neutral-500 font-mono">Envío</div>
                <NumberInput raw min="0" step="0.01" value={form.envio}
                  onChange={(e) => setForm(f => ({ ...f, envio: e.target.value }))}
                  className="w-full text-right text-xs border border-neutral-300 rounded px-1.5 py-1 font-mono mt-1"
                  data-testid="ped-envio" />
              </div>
              <div>
                <div className="text-[10px] uppercase text-neutral-500 font-mono">Descuento</div>
                <NumberInput raw min="0" step="0.01" value={form.descuento}
                  onChange={(e) => setForm(f => ({ ...f, descuento: e.target.value }))}
                  className="w-full text-right text-xs border border-neutral-300 rounded px-1.5 py-1 font-mono mt-1"
                  data-testid="ped-descuento" />
              </div>
              <div>
                <div className="text-[10px] uppercase text-neutral-500 font-mono">Total</div>
                <div className="font-mono text-base font-bold mt-1" data-testid="ped-total">{fmtMoney(formTotal)}</div>
              </div>
            </div>

            {/* Observaciones */}
            <Section title="Observaciones">
              <textarea rows={3} value={form.observaciones} onChange={(e) => setForm(f => ({ ...f, observaciones: e.target.value }))}
                placeholder="Notas internas, aclaraciones del cliente, indicaciones para la cocina, etc."
                className="w-full text-sm border border-neutral-300 rounded p-2 resize-none focus:border-emerald-500"
                data-testid="ped-observaciones" />
            </Section>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={submitForm} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="ped-guardar">
              {saving ? "Guardando..." : (editingId ? "Guardar cambios" : "Crear pedido")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Modal: Detalle ─── */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Detalle pedido #{detail?.numero}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="flex-1 overflow-y-auto pr-1 -mr-1 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <DField label="Fecha y hora" value={detail.hora ? `${detail.fecha} ${detail.hora} hs` : detail.fecha} mono />
                <DField label="Total" value={fmtMoney(detail.total, detail.moneda)} mono />
                <DField label="Cliente" value={detail.cliente_nombre} />
                <DField label="Email" value={detail.cliente_email} />
                <DField label="DNI/CUIT" value={detail.cliente_cuit} />
                <div>
                  <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest">Teléfono</div>
                  {detail.cliente_telefono ? (
                    <a href={waLinkForPedido(detail)} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-emerald-700 hover:underline font-mono text-sm mt-0.5">
                      <WhatsappLogo size={14} weight="fill" className="text-emerald-600" />
                      {fmtPhone(detail.cliente_telefono)}
                    </a>
                  ) : (<div className="text-sm text-neutral-400 mt-0.5">—</div>)}
                </div>
              </div>

              {/* Entrega */}
              <div className={`border rounded p-3 ${detail.entrega_tipo === "pickup" ? "border-blue-200 bg-blue-50/40" : "border-emerald-200 bg-emerald-50/30"}`}>
                <div className={`text-xs uppercase font-mono mb-2 font-bold ${detail.entrega_tipo === "pickup" ? "text-blue-700" : "text-emerald-700"}`}>
                  Entrega — {entregaInfo(detail).icon} {entregaInfo(detail).label}
                </div>
                {detail.entrega_tipo === "pickup" ? (
                  <div>{detail.entrega_sucursal ? `📍 ${detail.entrega_sucursal}` : "Retira en el local."}</div>
                ) : (
                  <div className="space-y-1">
                    {(detail.direccion_calle || detail.direccion_numero) ? (
                      <>
                        <div className="font-medium">
                          {detail.direccion_calle}{detail.direccion_numero ? ` ${detail.direccion_numero}` : ""}
                        </div>
                        <div className="text-xs text-neutral-600">
                          {[detail.direccion_localidad, detail.direccion_provincia,
                            detail.direccion_cp && `CP ${detail.direccion_cp}`].filter(Boolean).join(" · ")}
                        </div>
                      </>
                    ) : (
                      detail.direccion_entrega && <div>{detail.direccion_entrega}</div>
                    )}
                    {detail.entrega_costo != null && (
                      <div className="text-xs text-neutral-500">Costo envío: {fmtMoney(detail.entrega_costo)}</div>
                    )}
                  </div>
                )}
              </div>

              {/* Pago */}
              <div className="border border-emerald-200 rounded p-3 bg-emerald-50/30">
                <div className="text-xs uppercase text-emerald-700 font-mono mb-2 font-bold flex items-center gap-2">
                  Método de pago
                  <Badge variant="outline" className={`${(PAY_STATUS.find(x => x.value === detail.payment_status) || {}).cls} text-[10px]`}>
                    {(PAY_STATUS.find(x => x.value === detail.payment_status) || {}).label || "—"}
                  </Badge>
                </div>
                <div className="text-sm">{labelMetodoPago(detail.metodo_pago) || "—"}</div>
              </div>

              {/* Ítems */}
              <div>
                <div className="text-xs uppercase text-neutral-500 font-mono mb-1">Ítems</div>
                <div className="border border-neutral-200 rounded overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="text-left px-2 py-1">Producto</th>
                          <th className="text-right px-2 py-1">Cant.</th>
                          <th className="text-right px-2 py-1">P. Unit</th>
                          <th className="text-right px-2 py-1">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.items || []).map((it, i) => (
                          <tr key={i} className="border-t border-neutral-200">
                            <td className="px-2 py-1">
                              {it.name}
                              {it.sku && <span className="ml-2 text-neutral-400 font-mono">SKU:{it.sku}</span>}
                            </td>
                            <td className="text-right px-2 py-1 font-mono">{it.quantity}</td>
                            <td className="text-right px-2 py-1 font-mono">{fmtMoney(it.price, detail.moneda)}</td>
                            <td className="text-right px-2 py-1 font-mono">{fmtMoney(it.subtotal, detail.moneda)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                <div><span className="text-neutral-500">Subtotal:</span> <span className="font-mono">{fmtMoney(detail.subtotal, detail.moneda)}</span></div>
                <div><span className="text-neutral-500">Envío:</span> <span className="font-mono">{fmtMoney(detail.envio, detail.moneda)}</span></div>
                <div><span className="text-neutral-500">Descuento:</span> <span className="font-mono">-{fmtMoney(detail.descuento, detail.moneda)}</span></div>
                <div><span className="text-neutral-500">Total:</span> <span className="font-mono font-bold">{fmtMoney(detail.total, detail.moneda)}</span></div>
              </div>

              {detail.observaciones && (
                <div className="border border-amber-200 bg-amber-50/40 rounded p-3">
                  <div className="text-[11px] uppercase text-amber-700 font-mono font-bold mb-1">Observaciones</div>
                  <div className="text-sm whitespace-pre-wrap">{detail.observaciones}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>Cerrar</Button>
            {detail && detail.estado_local !== "terminado" && detail.estado_local !== "anulado" && (
              <Button variant="outline" onClick={() => { const p = detail; setDetail(null); openEdit(p); }} className="text-blue-700 border-blue-300 hover:bg-blue-50">
                <Pencil size={14} className="mr-1" /> Editar
              </Button>
            )}
            {detail?.anulada_por_nc_numero ? (
              <Badge
                variant="outline"
                className="bg-neutral-200 text-neutral-700 border-neutral-400 h-9 flex items-center px-3"
                data-testid="ped-detalle-nc-badge"
                title={`Anulada con NC ${detail.anulada_por_nc_numero}`}
              >
                ⊘ NC emitida · N° {detail.anulada_por_nc_numero}
              </Badge>
            ) : detail?.factura_id ? (
              <Button
                variant="outline"
                className="text-rose-700 border-rose-300 hover:bg-rose-50"
                onClick={() => { setNcTarget(detail); setNcMotivo(""); }}
                data-testid="ped-detalle-nc-btn"
              >
                <XCircle size={14} className="mr-1" /> Generar NC
              </Button>
            ) : (
              detail && (
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  onClick={() => { const id = detail.id; setDetail(null); navigate(`/ventas?pedido_id=${id}`); }}
                  data-testid="ped-detalle-facturar">
                  Facturar pedido →
                </Button>
              )
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Confirm delete ─── */}
      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle size={20} /> Eliminar pedido #{delTarget?.numero}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-700">
            ¿Estás seguro? Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={eliminar}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ─── Confirm NC ─── */}
      <Dialog open={!!ncTarget} onOpenChange={(o) => { if (!o) { setNcTarget(null); setNcMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="ped-nc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <XCircle size={20} /> Emitir Nota de Crédito
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <p>
              Se emitirá una <strong>Nota de Crédito</strong> anulando el comprobante{" "}
              <span className="font-mono font-bold">{ncTarget?.factura_numero || ncTarget?.factura_id}</span>{" "}
              del pedido <span className="font-mono font-bold">#{ncTarget?.numero}</span>.
            </p>
            <p className="text-neutral-600 text-xs">
              Si la factura tiene CAE (A/B/C) se emitirá una <strong>NC electrónica en AFIP</strong> con el motivo indicado.
              Si es un Remito / comprobante no fiscal, se generará una <strong>Nota de Crédito X</strong> interna.
              El pedido pasará a estado <strong>Anulado</strong> y los cobros aplicados serán revertidos.
            </p>
            <div>
              <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest mb-1">Motivo (opcional, solo para NC electrónica)</div>
              <Input
                value={ncMotivo}
                onChange={(e) => setNcMotivo(e.target.value)}
                placeholder="Ej: Cliente devolvió mercadería"
                data-testid="ped-nc-motivo"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNcTarget(null); setNcMotivo(""); }} disabled={ncSubmitting}>
              Cancelar
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={emitirNC}
              disabled={ncSubmitting}
              data-testid="ped-nc-emitir-btn"
            >
              {ncSubmitting ? "Emitiendo NC..." : "Emitir Nota de Crédito"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PdfPreviewDialog
        open={!!previewPed}
        onOpenChange={(o) => { if (!o) setPreviewPed(null); }}
        url={previewPed ? (previewPed.factura_id && !previewPed.anulada_por_nc_id ? `/afip/pdf/${previewPed.factura_id}` : `/pedidos/${previewPed.id}/pdf`) : null}
        title={previewPed ? (previewPed.factura_id && !previewPed.anulada_por_nc_id ? `Factura · Pedido #${previewPed.numero}` : `Pedido #${previewPed.numero}`) : ""}
        filename={previewPed ? `Pedido_${previewPed.numero}.pdf` : undefined}
      />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, colorClass = "text-neutral-800", active = false, onClick }) {
  const cls = onClick ? "cursor-pointer transition-all hover:border-neutral-500 hover:shadow-sm" : "";
  const activeCls = active ? "border-neutral-800 ring-2 ring-neutral-200 shadow" : "";
  return (
    <div className={`border border-neutral-300 rounded bg-white px-4 py-3 flex items-center gap-3 ${cls} ${activeCls}`}
      onClick={onClick}
      data-testid={onClick && label ? `stat-${label.replace(/\s+/g, "-").toLowerCase()}` : undefined}
    >
      <Icon size={22} weight="regular" className={colorClass} />
      <div>
        <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest">{label}</div>
        <div className={`text-xl font-bold ${colorClass}`}>{value}</div>
      </div>
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] uppercase text-neutral-500 font-mono tracking-widest font-semibold">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest mb-0.5">{label}</div>
      {children}
    </div>
  );
}

function DField({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
